import React, { useEffect, useMemo, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import {
  APPLICANTS_QUERY,
  CREATE_INTERVIEW,
  mapApplicant,
} from '../data/recruitmentApi';
import '../styles/AddApplicant.css';

function toLocalInputValue(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const defaultWhen = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return toLocalInputValue(d);
};

export default function ScheduleInterview({
  onClose,
  onCreated,
  preselectedApplicantId = '',
}) {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applicantId, setApplicantId] = useState(preselectedApplicantId || '');
  const [interviewer, setInterviewer] = useState('');
  const [type, setType] = useState('video');
  const [scheduledAt, setScheduledAt] = useState(defaultWhen);
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await graphqlRequest(APPLICANTS_QUERY);
        const rows = Array.isArray(data?.recruitmentApplicants)
          ? data.recruitmentApplicants.map(mapApplicant)
          : [];
        if (!cancelled) setApplicants(rows);
      } catch (err) {
        toast.error(err?.message || 'Could not load applicants.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (preselectedApplicantId) setApplicantId(preselectedApplicantId);
  }, [preselectedApplicantId]);

  const selected = useMemo(
    () => applicants.find((a) => a.id === applicantId) || null,
    [applicants, applicantId],
  );

  useEffect(() => {
    if (!selected) return;
    setInterviewer((prev) => prev || selected.recruiter || '');
    setLocation((prev) => prev || selected.branch || '');
  }, [selected]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected) {
      toast.error('Please select an applicant');
      return;
    }
    if (!scheduledAt) {
      toast.error('Please choose date and time');
      return;
    }

    setSaving(true);
    try {
      const data = await graphqlRequest(CREATE_INTERVIEW, {
        input: {
          applicantId: selected.id,
          name: selected.name,
          role: selected.role,
          interviewer: interviewer.trim() || null,
          type,
          scheduledAt: new Date(scheduledAt).toISOString(),
          location: location.trim() || null,
          notes: notes.trim() || null,
          moveApplicantToInterview: true,
        },
      });
      const created = data?.createRecruitmentInterview;
      if (!created?.id) throw new Error('Interview was not created.');
      toast.success('Interview scheduled.');
      onCreated?.(created);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Could not schedule interview.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="aa-overlay" onClick={onClose}>
      <div
        className="aa-modal"
        role="dialog"
        aria-labelledby="si-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="aa-header">
          <div>
            <h2 id="si-title">Schedule Interview</h2>
            <p>Pick an applicant, interviewer, date, and location.</p>
          </div>
          <button
            type="button"
            className="aa-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>

        {loading ? (
          <p className="aa-hint">Loading applicants…</p>
        ) : (
          <form className="aa-form" onSubmit={handleSubmit}>
            <label className="aa-field aa-field--full">
              <span>Applicant *</span>
              <select
                value={applicantId}
                onChange={(e) => setApplicantId(e.target.value)}
                required
              >
                <option value="">Select applicant</option>
                {applicants.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} — {a.role}
                  </option>
                ))}
              </select>
            </label>

            <div className="aa-grid">
              <label className="aa-field">
                <span>Interviewer</span>
                <input
                  type="text"
                  value={interviewer}
                  onChange={(e) => setInterviewer(e.target.value)}
                  placeholder="e.g. Emily Watson"
                />
              </label>

              <label className="aa-field">
                <span>Type</span>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                >
                  <option value="video">Video Call</option>
                  <option value="in_person">In-Person</option>
                  <option value="phone">Phone</option>
                </select>
              </label>

              <label className="aa-field">
                <span>Date & time *</span>
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  required
                />
              </label>

              <label className="aa-field">
                <span>Location / link</span>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Office / Meet link"
                />
              </label>
            </div>

            <label className="aa-field aa-field--full">
              <span>Notes</span>
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional interview notes…"
              />
            </label>

            <div className="aa-actions">
              <button
                type="button"
                className="aa-btn aa-btn--ghost"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="aa-btn aa-btn--primary"
                disabled={saving || !applicants.length}
              >
                {saving ? 'Scheduling…' : 'Schedule Interview'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
